import type { ReactNode } from "react";

import type { MeqColumnDef, MeqRowData } from "~/components/MeqTable/types";
import type { LooseObject } from "~/utils/Types";
import type { UsdaGradeBandId } from "~/utils/usdaGradeBands";

export type ImagesModalMode = "grid" | "table";

/**
 * Row keys the modal reads values through. `id`/`date`/`imgUrl`/`keyframeHash`/
 * `location` are passed straight to the grid and the image viewer; the rest
 * back a filter and also decide whether that filter renders at all.
 */
export type ImagesModalRowKeys = {
  ausMarbling: string;
  bodyId: string;
  date: string;
  dcuLite: string;
  ema: string;
  emaOverride: string;
  fatColor: string;
  grader: string;
  id: string;
  imgUrl: string;
  keyframeHash: string;
  killDate: string;
  location: string;
  meatColor: string;
  msa: string;
  sex: string;
  usdaMarbling: string;
};

export const DEFAULT_ROW_KEYS: ImagesModalRowKeys = {
  ausMarbling: "AUS_MARBLING",
  bodyId: "BODY_NUMBER",
  date: "PHOTO_DATETIME",
  dcuLite: "IS_DCULITE",
  ema: "EYE_MUSCLE_AREA",
  emaOverride: "OVERRIDE_EYE_MUSCLE_AREA",
  fatColor: "FAT_COLOR",
  grader: "USER",
  id: "BARCODE",
  imgUrl: "AZ_URL",
  keyframeHash: "KEYFRAME_HASH",
  killDate: "KILL_DATE",
  location: "LOCATION",
  meatColor: "MEAT_COLOR",
  msa: "MSA_MARBLING",
  sex: "SEX",
  usdaMarbling: "USDA_MARBLING",
};

/**
 * Whether a carcase was graded on MSA is read as "carries an MSA marbling
 * score" — at a DCU site the score is present only for bodies the DCU flagged
 * as MSA, which is the distinction operators are being asked about.
 */
export type ImagesMsaFilter = "all" | "msa" | "nonMsa";

/**
 * A DCU Lite carcase is one the flag marks `true`. Null is standard, not a third
 * state: the column is `Nullable(Bool)` and reads null for anything captured
 * before the flag existed, all of which came off a DCU with images.
 */
export type ImagesDcuLiteFilter = "all" | "dcuLite" | "standard";

/**
 * Every value filter is a chosen set of the column's own values, matched by
 * their string form, except the USDA grade: the feed carries the marbling
 * score, and the filter offers the grade bands it falls into (see
 * `~/utils/usdaGradeBands`), which is what a US reader asks for.
 */
export type ImagesFilterState = {
  ausMarblings: string[];
  bodyIdFrom: number | "";
  bodyIdTo: number | "";
  dcuLite: ImagesDcuLiteFilter;
  fatColors: string[];
  graders: string[];
  isEmaMissedOnly: boolean;
  meatColors: string[];
  msaGrading: ImagesMsaFilter;
  sexes: string[];
  usdaGrades: UsdaGradeBandId[];
};

export const EMPTY_FILTER_STATE: ImagesFilterState = {
  ausMarblings: [],
  bodyIdFrom: "",
  bodyIdTo: "",
  dcuLite: "all",
  fatColors: [],
  graders: [],
  isEmaMissedOnly: false,
  meatColors: [],
  msaGrading: "all",
  sexes: [],
  usdaGrades: [],
};

/** Which filters the resolved column set supports, so the popover offers no
 *  control over a value this preset does not put on screen. */
export type ImagesFilterAvailability = {
  hasAusMarbling: boolean;
  hasBodyId: boolean;
  hasDcuLite: boolean;
  hasEma: boolean;
  hasFatColor: boolean;
  hasGrader: boolean;
  hasMeatColor: boolean;
  hasMsa: boolean;
  hasSex: boolean;
  hasUsdaGrade: boolean;
};

/**
 * What the filters have left, for a caller composing its own title. Both keys
 * are absent while nothing is narrowing; `filterLabel` is absent when several
 * filters are on, since there is no one name for that.
 */
export type ImagesContentTitleContext = {
  matched?: number;
  filterLabel?: string;
};

/**
 * The selection that brought the reader here — a pie slice, a producer bubble,
 * a grading decision. Applied before the popover filters and shown as a
 * removable chip, so what narrowed the set is visible and can be widened to
 * every row the caller passed without leaving the modal.
 */
export type ImagesDrillFilter = {
  label: string;
  matches: (row: LooseObject) => boolean;
};

/**
 * The one sort both modes share: the grid's sort control and the table's
 * column headers read and write the same state, so switching mode never
 * changes the order the reader was looking at.
 */
export type ImagesSortState = {
  /** Row key the values are read from, not the MeqTable column id. */
  columnKey: string;
  isAscending: boolean;
};

/**
 * Sort seed. Shaped to the v2 individual-images `defaultSort` so a caller can
 * forward the server's value unchanged; `undefined` (the server omits the key
 * when nothing resolves) leaves both modes unsorted.
 */
export type ImagesModalDefaultSort = {
  name: string;
  isAscending: boolean;
};

export type ImagesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Caller-owned — the modal never derives a title from the data. */
  title: string;
  /** Dimmed line under the title. */
  subtitle?: ReactNode;
  /** Heads the content in both modes — the table toolbar's leading slot in
   *  list mode, and the line above the tiles in image mode. Pass a function to
   *  fold the filters' effect into the caller's own wording; the filters live
   *  in here, so the caller cannot count them itself. */
  contentTitle?:
    | ReactNode
    | ((context: ImagesContentTitleContext) => ReactNode);
  /**
   * Caller-owned chrome in the title row, left of the mode toggle (the
   * Summary-of-Images stat strip). Deliberately not built in: the other
   * consumers have no equivalent.
   */
  header?: ReactNode;

  /** The display set AND the render order. Row key order carries no meaning. */
  columns: MeqColumnDef<MeqRowData>[];
  rows: LooseObject[];
  isLoading?: boolean;

  defaultMode?: ImagesModalMode;
  defaultSort?: ImagesModalDefaultSort;

  ausMarblingKey?: string;
  bodyIdKey?: string;
  dateKey?: string;
  dcuLiteKey?: string;
  emaKey?: string;
  emaOverrideKey?: string;
  fatColorKey?: string;
  graderKey?: string;
  idKey?: string;
  imgUrlKey?: string;
  keyframeHashKey?: string;
  killDateKey?: string;
  locationKey?: string;
  meatColorKey?: string;
  msaKey?: string;
  sexKey?: string;
  usdaMarblingKey?: string;

  /** Row key behind each tile's headline number. Omitted renders no badge —
   *  the caller decides which trait leads, since it varies by region. */
  tileScoreKey?: string;
  /** Label for that number, surfaced on hover. */
  tileScoreLabel?: string;

  /** Extra caller controls, rendered inside the one filter popover. */
  filterControls?: ReactNode;
  /** Pre-applied on every open; cleared from its chip. See `ImagesDrillFilter`. */
  drillFilter?: ImagesDrillFilter;
  /** Per-tile badge slot. */
  renderItemOverlay?: (item: LooseObject) => ReactNode;
  /** Drives the table toolbar's own export control. `rows` is the table's
   *  resolved set, so the file matches what is on screen. List mode only —
   *  the grid has no export. */
  onExportCsv?: (context: { rows: LooseObject[] }) => void;

  /**
   * Extra trait rows shown ONLY in the single-image viewer's detail panel —
   * distinct from `columns`, which also drives the table's columns and the
   * grid's sort-by list. Use this for fields available solely through
   * `useItemDetail`'s per-item fetch, not present on every row in bulk (so
   * table mode and grid sorting never reference a field they have no data
   * for). Pass together with `useItemDetail`; meaningless alone.
   */
  detailColumns?: MeqColumnDef<MeqRowData>[];
  /**
   * Per-item async detail fetch backing `detailColumns`, for callers whose
   * rows don't carry everything the single-image viewer needs. It's a hook —
   * the viewer calls it every render with the currently-shown item, so it
   * re-fires on open and on every prev/next navigation, exactly like any
   * other hook reacting to a changed argument. Each item's result is cached
   * independently by whatever key the caller's own hook uses (e.g. a
   * TanStack Query key derived from the item's id), so navigating quickly
   * can never show one item's fetched data on a different item — no manual
   * staleness guard needed on the caller's side either.
   */
  useItemDetail?: (item: LooseObject) => {
    data?: LooseObject;
    isLoading?: boolean;
  };
};
